import React, { useState } from "react";
import { MeasurementResult } from "../../../utilities/types/measurement-data/types";
import { getValidMeasurements } from "../../../utilities/helper/filterValidMeasurements";
import UnitToggle from "../UnitToggle";

interface BodyMeasurementResultDisplayProps {
  data: MeasurementResult;
}

const CM_TO_INCH = 0.393701;

const BodyMeasurementResultDisplay: React.FC<BodyMeasurementResultDisplayProps> = ({ data }) => {
  const [selectedUnit, setSelectedUnit] = useState<"cm" | "inch">("cm");
  const validMeasurements = getValidMeasurements(data);

  const convertValue = (value: number): string => {
    if (selectedUnit === "inch") {
      return (value * CM_TO_INCH).toFixed(2);
    }
    return value.toFixed(2);
  };

  const handleUnitChange = (unit: "cm" | "inch") => {
    setSelectedUnit(unit);
  };

  return (
    <div className="flex flex-col items-center w-full gap-6">
      {data.MetaData && (
        <div className="w-full max-w-2xl p-5 border rounded-lg bg-gradient-to-br from-gorgonzola-blue-50/30 to-white border-gorgonzola-blue-100">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 pb-2 border-b border-gorgonzola-blue-100">
              <span className="text-xs font-semibold text-gorgonzola-blue-400">Gender:</span>
              <span className="text-sm font-medium text-closed-shutter-400">{data.Gender || "N/A"}</span>
            </div>
            <div className="flex items-center gap-2 pb-2 border-b border-gorgonzola-blue-100">
              <span className="text-xs font-semibold text-gorgonzola-blue-400">Body Type:</span>
              <span className="text-sm font-medium text-closed-shutter-400">{data.BodyType || "N/A"}</span>
            </div>
            {data.MetaData.Date && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gorgonzola-blue-400">Date:</span>
                <span className="text-sm font-medium text-closed-shutter-400">{data.MetaData.Date}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center">
        <UnitToggle selectedUnit={selectedUnit} onUnitChange={handleUnitChange} />
      </div>

      <div className="w-full max-w-2xl">
        <div className="grid w-full grid-rows-3 gap-1 p-3 border rounded-md border-gorgonzola-blue-50">
          {validMeasurements.map((measurement, index) => (
            <div key={measurement.key} className={`flex justify-between gap-1 w-full *:rounded *:p-2 ${index % 2 === 0 ? "*:bg-gorgonzola-blue-50/20" : "*:bg-gorgonzola-blue-50/50"}`}>
              <span className="flex items-center">{measurement.icon && <img src={measurement.icon} alt={`${measurement.label} icon`} className="size-5 min-w-5" />}</span>

              <span className="text-xs md:text-base flex-grow md:min-w-[11.25rem] line-clamp-1">{measurement.label}</span>

              <span className="text-xs md:text-base w-[6.2rem] md:w-36 line-clamp-1">
                {convertValue(measurement.value)} {selectedUnit}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BodyMeasurementResultDisplay;
